import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';
import { isTransientFailure, jitteredStaleTime } from '~/utils/jitter';

import { groupPreScoreRows, toPreScoreRow, withPreScores } from '../mappers/run-gate.mapper';
import {
  type EnrichedProductsFilter,
  type EnrichmentPhase,
  getEnrichedProducts,
  getFailedProducts,
  getProductScoreDetails,
  getRunScorecard,
  getRunScores,
  getSamplePreview,
  getSampleQualityScore,
  type ScorePhase,
} from '../services/run-content.service';
import { getCurationRunProducts } from '../services/run-detail.service';
import type { CatalogPage, ContentPage } from '../types/catalog-enrichment.types';
import type {
  FailedProduct,
  ProductScore,
  RunEnrichedProduct,
  RunScorecard,
  SamplePreviewProduct,
  ScorePage,
} from '../types/run-content.api.types';
import type { PreScoreTable } from '../types/run-gate.types';

/**
 * Content-generation reads on the gate screens. They are optional context for a
 * decision, so each retries once — only on a transient failure (5xx / 408 / 429 /
 * network), like the app-wide default; a 404/4xx fails fast — after a full-jitter backoff
 * (the catalog-service query default, `fullJitterRetryDelay`), and then surfaces
 * `isError` for a "content unavailable" note. They never block the decision bar.
 */
function retryContentRead(failureCount: number, error: unknown): boolean {
  return isTransientFailure(error) && failureCount < 1;
}

/** PRE / POST score roll-up for the run (PRE score gate). */
/**
 * Scores at a waiting gate do not change until someone decides, so gate reads are kept fresh for
 * a few minutes: switching tabs or coming back to Review is instant instead of a 2–3 s refetch.
 */
export const GATE_CONTENT_STALE_MS = 5 * 60_000;

/** Query options for the run's scorecard — shared by the hook and the run shell's prefetch. */
export function runScorecardQuery(runRef: string) {
  return {
    queryKey: queryKeys.catalogService.runContent(runRef, 'scorecard'),
    queryFn: () => getRunScorecard(runRef),
    staleTime: jitteredStaleTime(GATE_CONTENT_STALE_MS),
    retry: retryContentRead,
  };
}

/** How often a gate re-reads a scorecard whose phase content generation has not published yet. */
export const SCORES_ARRIVING_POLL_MS = 10_000;

/**
 * The run's scorecard. `awaitPhase` is for a score gate: the gate opens when content generation's
 * callback says scoring finished, but its read can trail that by a minute or two — the phase comes
 * back `null`, and the 5-minute gate stale time would keep that `null` on screen. While the awaited
 * phase is missing (and the read itself succeeded) the query polls; it stops once the phase lands.
 * One shared query key, so every reader of the scorecard (headline, list counts) updates with it.
 */
export function useRunScorecard(
  runRef: string,
  enabled = true,
  options: { awaitPhase?: 'pre' | 'post' } = {},
) {
  const { awaitPhase } = options;
  return useQuery({
    ...runScorecardQuery(runRef),
    enabled: enabled && !!runRef,
    // A 404 read (`null`: nothing published for the run yet) waits the same way.
    refetchInterval: (query) =>
      awaitPhase !== undefined &&
      query.state.status === 'success' &&
      (query.state.data?.[awaitPhase] ?? null) === null
        ? SCORES_ARRIVING_POLL_MS
        : false,
  });
}

/** A score gate's read state: the phase, still arriving (read fine, phase not published), or failed. */
export function scorePhaseState(
  query: { isLoading: boolean; isError: boolean; data: RunScorecard | null | undefined },
  phase: 'pre' | 'post',
): 'loading' | 'ready' | 'arriving' | 'unavailable' {
  if (query.isLoading) return 'loading';
  if (query.isError) return 'unavailable';
  if (query.data?.[phase]) return 'ready';
  // A degraded read names why content generation could not answer: that is a failure, not lag.
  // No scorecard yet (404) or a phase still null is the read trailing the callback.
  return query.data?.degraded ? 'unavailable' : 'arriving';
}

/**
 * One page of the run's curated feed products with their PRE score. content-gen's
 * `/scores` needs explicit product ids, so the page comes from the run's curation
 * products (unflagged only — flagged rows are never scored) and is joined by
 * catalog product id. A failed score read still renders the rows, flagged. The
 * curation page is per FEED row, so a product curated into several markets (US +
 * UK) can land twice in one page — `groupPreScoreRows` collapses those into one
 * row before this resolves (Q20).
 */
/** Rows per page of the pre-score table — shared so the run shell can prefetch page 1. */
export const PRE_SCORE_PAGE_SIZE = 20;

/** Query options for one pre-score page — shared by the hook and the run shell's prefetch. */
export function runPreScoresQuery(runRef: string, page: number, size: number) {
  return {
    queryKey: queryKeys.catalogService.runContent(runRef, 'pre-scores', { page, size }),
    queryFn: async (): Promise<PreScoreTable> => {
      const products = await getCurationRunProducts(runRef, {
        page,
        size,
        needsAttention: false,
        sortBy: 'title',
        // Names, ids, images and gallery only — `summary` drops descriptions and raw payloads.
        fields: 'summary',
      });
      const rows = products.items.map(toPreScoreRow);
      const ids = rows.flatMap((r) => (r.productId ? [r.productId] : []));
      let scoresUnavailable: string | null = null;
      let scored = rows;
      if (ids.length > 0) {
        try {
          // R7: ask why each unscored product has no score (ignored by an older content-gen).
          const scores = await getRunScores(runRef, ids, 'PRE', { missingReasons: true });
          scored = withPreScores(rows, scores);
          scoresUnavailable = scores?.degraded ?? null;
        } catch {
          // Scores are content-gen's; keep the catalog rows and flag scores as missing (the
          // note shows a plain sentence, never the raw server text).
          scoresUnavailable = 'unavailable';
        }
      }
      return {
        // Q20: one row per catalog product — collapses the US/UK/… feed rows of the
        // same product (identical score, joined by productId) into one row.
        rows: groupPreScoreRows(scored),
        total: products.pagination.total,
        page: products.pagination.page,
        size: products.pagination.size,
        scoresUnavailable,
      };
    },
    staleTime: jitteredStaleTime(GATE_CONTENT_STALE_MS),
    retry: retryContentRead,
  };
}

export function useRunPreScores(runRef: string, page: number, size: number, enabled = true) {
  return useQuery({
    ...runPreScoresQuery(runRef, page, size),
    enabled: enabled && !!runRef,
    placeholderData: keepPreviousData,
  });
}

/** A page of the SAMPLE gate's before / after rows. */
export function useRunSamplePreview(runRef: string, page: number, size: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogService.runContent(runRef, 'sample-preview', { page, size }),
    queryFn: () => getSamplePreview(runRef, { page, size }),
    enabled: enabled && !!runRef,
    placeholderData: keepPreviousData,
    retry: retryContentRead,
  });
}

/** Page size and page cap walking the WHOLE sample set (it's a deliberately small subset). */
const SAMPLE_WALK_PAGE_SIZE = 100;
const SAMPLE_WALK_MAX_PAGES = 20;

/**
 * Every sampled product, keyed by catalog product id — the Products tab's own Sample column
 * reads this (batched once per run, not once per row) to show this product's real before/after/
 * lift/status instead of a run-level pill. The sample is a deliberately small, bounded subset
 * (unlike the full curated set), so walking every page of it is cheap and cached once.
 */
export function useRunSampleMembers(runRef: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogService.runContent(runRef, 'sample-members'),
    queryFn: async (): Promise<Map<string, SamplePreviewProduct>> => {
      const byId = new Map<string, SamplePreviewProduct>();
      for (let page = 1; page <= SAMPLE_WALK_MAX_PAGES; page++) {
        const result = await getSamplePreview(runRef, { page, size: SAMPLE_WALK_PAGE_SIZE });
        for (const row of result.items) byId.set(row.productId, row);
        if (page >= result.totalPages) break;
      }
      return byId;
    },
    enabled: enabled && !!runRef,
    staleTime: jitteredStaleTime(GATE_CONTENT_STALE_MS),
    retry: retryContentRead,
  });
}

/** The sample's headline before / after / lift. */
export function useRunSampleQualityScore(runRef: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogService.runContent(runRef, 'sample-quality-score'),
    queryFn: () => getSampleQualityScore(runRef),
    enabled: enabled && !!runRef,
    retry: retryContentRead,
  });
}

/** Item-retry stage → the content-generation enrichment phase whose failures it re-runs. */
const RETRY_STAGE_PHASE: Record<string, EnrichmentPhase> = { SAMPLE: 'SAMPLE', ENRICHMENT: 'FULL' };

/** Page size and page cap when collecting every retryable failure (100 × 20 = 2,000 ids). */
const ALL_FAILED_PAGE_SIZE = 100;
const ALL_FAILED_MAX_PAGES = 20;

/** The stages whose failed products content-generation can list (SAMPLE / ENRICHMENT). */
export function hasFailedProductLedger(stage: string): boolean {
  return stage in RETRY_STAGE_PHASE;
}

/**
 * Every RETRYABLE failed product for one item-retry stage (SAMPLE / ENRICHMENT), walked
 * page by page. The catalog's item-retry contracts take an explicit id list (the gate's
 * StageDecision has no "all" mode), so "Retry all failed" sends these ids and the recovery
 * dialog lists them to pick from. `truncated` is set when the page cap stopped the walk.
 */
export function useRunRetryableFailedProducts(runRef: string, retryStage: string, enabled = true) {
  const phase = RETRY_STAGE_PHASE[retryStage];
  return useQuery({
    queryKey: queryKeys.catalogService.runContent(runRef, 'failed-products-all', {
      phase: phase ?? null,
    }),
    queryFn: async (): Promise<{ products: FailedProduct[]; truncated: boolean }> => {
      const byId = new Map<string, FailedProduct>();
      let first: ContentPage<FailedProduct> | undefined;
      for (let page = 1; page <= ALL_FAILED_MAX_PAGES; page++) {
        const result = await getFailedProducts(runRef, {
          page,
          size: ALL_FAILED_PAGE_SIZE,
          retryable: true,
          ...(phase ? { phase } : {}),
          ...walkCountParams(page),
        });
        first ??= result;
        for (const row of result.items) if (row.retryable) byId.set(row.productId, row);
        if (!hasPageAfter(result, first, ALL_FAILED_PAGE_SIZE)) {
          return { products: [...byId.values()], truncated: false };
        }
      }
      return { products: [...byId.values()], truncated: true };
    },
    enabled: enabled && !!runRef && !!phase,
    retry: retryContentRead,
  });
}

/**
 * One product's score breakdown for the B1 side panel — fetched only while the panel is
 * open (`productId` set), so a table of products never fans out into per-row calls.
 * `onDemand` reads the set an on-demand re-score wrote (after a re-score this session).
 */
export function useRunProductScoreDetails(
  runRef: string,
  productId: string | null,
  phase: ScorePhase,
  onDemand = false,
) {
  return useQuery({
    queryKey: queryKeys.catalogService.runContent(runRef, 'score-details', {
      productId,
      phase,
      onDemand,
    }),
    queryFn: () => getProductScoreDetails(runRef, productId ?? '', phase, onDemand),
    enabled: !!runRef && !!productId,
    retry: retryContentRead,
  });
}

/** One enriched product's 0-100 quality: PRE (before enrichment) and POST (null until scored). */
export interface EnrichedQuality {
  pre: number | null;
  post: number | null;
}

/** `overall` off one phase's score page, keyed by catalog product id. */
function overallById(page: ScorePage | null): Map<string, number> {
  const byId = new Map<string, number>();
  for (const p of page?.products ?? []) {
    const overall = p.scores.overall;
    if (typeof overall === 'number') byId.set(p.productId, overall);
  }
  return byId;
}

/**
 * PRE / POST quality (0-100) for one page of the ENRICHMENT gate's products: two batched
 * `/scores` reads per page (PRE + POST, the ids of that page), never one per row. POST lands
 * after the gate, so a failed or empty POST read just leaves `post` null; only when both
 * phases fail does the query error.
 */
export function useRunEnrichedQuality(runRef: string, productIds: string[], enabled = true) {
  const ids = [...new Set(productIds)].sort();
  return useQuery({
    queryKey: queryKeys.catalogService.runContent(runRef, 'enriched-quality', { ids }),
    queryFn: async (): Promise<Map<string, EnrichedQuality>> => {
      const [pre, post] = await Promise.allSettled([
        getRunScores(runRef, ids, 'PRE'),
        getRunScores(runRef, ids, 'POST'),
      ]);
      if (pre.status === 'rejected' && post.status === 'rejected') throw pre.reason;
      const preById = overallById(pre.status === 'fulfilled' ? pre.value : null);
      const postById = overallById(post.status === 'fulfilled' ? post.value : null);
      return new Map(
        ids.map((id) => [id, { pre: preById.get(id) ?? null, post: postById.get(id) ?? null }]),
      );
    },
    enabled: enabled && !!runRef && ids.length > 0,
    placeholderData: keepPreviousData,
    staleTime: jitteredStaleTime(GATE_CONTENT_STALE_MS),
    retry: retryContentRead,
  });
}

/**
 * Enrichment review walk (Levels 1–3). content-generation has no aggregate / reason / risk-sorted
 * endpoints, so the review reads a CAPPED sample client-side: at most `REVIEW_SAMPLE_CAP` products,
 * `REVIEW_PAGE_SIZE` per page. The pages are fetched one at a time by the review hook (lazy, with a
 * jittered pause between pages, stoppable) — these queries only describe one page each.
 */
export const REVIEW_SAMPLE_CAP = 1000;
export const REVIEW_PAGE_SIZE = 100;
/** `/scores` takes ids in the query string — 100 UUIDs keeps the URL well under 8 KB. */
const SCORE_ID_CHUNK = 100;

/** One sampled page of enriched products with the PRE / POST score rows of its ids. */
export interface EnrichedReviewPage {
  page: ContentPage<RunEnrichedProduct>;
  pre: Map<string, ProductScore>;
  post: Map<string, ProductScore>;
  /** True when a score read failed — the page still renders, scores show as missing. */
  scoresUnavailable: boolean;
}

/** Score rows keyed by product id, read in URL-safe chunks; a failed chunk reports `failed`. */
async function scoreRowsById(
  runRef: string,
  ids: string[],
  phase: 'PRE' | 'POST',
  signal: AbortSignal,
): Promise<{ byId: Map<string, ProductScore>; failed: boolean }> {
  const byId = new Map<string, ProductScore>();
  let failed = false;
  for (let i = 0; i < ids.length; i += SCORE_ID_CHUNK) {
    // Leaving the review aborts the walk: no further chunk is requested.
    signal.throwIfAborted();
    try {
      const page = await getRunScores(runRef, ids.slice(i, i + SCORE_ID_CHUNK), phase);
      for (const p of page?.products ?? []) byId.set(p.productId, p);
    } catch {
      failed = true;
    }
  }
  return { byId, failed };
}

/**
 * Whether a walked content-gen page has a page after it. A short page is the last one (the lists
 * are one row per product, cut in SQL); otherwise the server's own `hasNextPage` — exact even for
 * an uncounted (`includeTotal=false`) page — and, on a content-gen without it, the page count
 * (this page's, or the counted first page's when this one skipped its total).
 */
function hasPageAfter<T>(
  last: ContentPage<T>,
  first: ContentPage<T> | undefined,
  size: number,
): boolean {
  if (last.items.length < size) return false;
  if (last.hasNextPage !== null) return last.hasNextPage;
  return last.page < (last.totalPages || (first?.totalPages ?? 0));
}

/** Next 1-based page while the server has more AND the sample is under the cap. */
function nextSamplePage<T>(
  last: ContentPage<T>,
  first: ContentPage<T> | undefined,
  pages: number,
): number | undefined {
  return pages * REVIEW_PAGE_SIZE < REVIEW_SAMPLE_CAP && hasPageAfter(last, first, REVIEW_PAGE_SIZE)
    ? last.page + 1
    : undefined;
}

/** Only a walk's FIRST page pays for the whole-run count; later pages reuse its total. */
function walkCountParams(page: number): { includeTotal?: false } {
  return page > 1 ? { includeTotal: false } : {};
}

/**
 * The review's enriched-products sample, one page per fetch: each page brings its own PRE + POST
 * score rows (two chunked `/scores` reads), so a page renders complete. POST is empty until the run
 * is re-scored; a failed score read leaves the page usable with `scoresUnavailable`.
 */
export function useRunEnrichedReviewPages(runRef: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: queryKeys.catalogService.runContent(runRef, 'review-enriched', {
      size: REVIEW_PAGE_SIZE,
    }),
    // Reading `signal` opts into TanStack's abort: when the review unmounts mid-page the query is
    // cancelled (reverted, pages already read stay cached) and no further request is made.
    queryFn: async ({ pageParam, signal }): Promise<EnrichedReviewPage> => {
      const page = await getEnrichedProducts(runRef, {
        page: pageParam,
        size: REVIEW_PAGE_SIZE,
        ...walkCountParams(pageParam),
      });
      signal.throwIfAborted();
      const ids = [...new Set(page.items.map((r) => r.productId))];
      const [pre, post] = await Promise.all([
        scoreRowsById(runRef, ids, 'PRE', signal),
        scoreRowsById(runRef, ids, 'POST', signal),
      ]);
      return { page, pre: pre.byId, post: post.byId, scoresUnavailable: pre.failed && post.failed };
    },
    initialPageParam: 1,
    getNextPageParam: (last, all) => nextSamplePage(last.page, all[0]?.page, all.length),
    enabled: enabled && !!runRef,
    staleTime: jitteredStaleTime(GATE_CONTENT_STALE_MS),
    refetchOnWindowFocus: false,
    retry: retryContentRead,
  });
}

/** The review's failed-products sample (capped like the enriched one), one page per fetch. */
export function useRunFailedReviewPages(runRef: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: queryKeys.catalogService.runContent(runRef, 'review-failed', {
      size: REVIEW_PAGE_SIZE,
    }),
    queryFn: async ({ pageParam, signal }) => {
      const page = await getFailedProducts(runRef, {
        page: pageParam,
        size: REVIEW_PAGE_SIZE,
        ...walkCountParams(pageParam),
      });
      signal.throwIfAborted();
      return page;
    },
    initialPageParam: 1,
    getNextPageParam: (last, all) => nextSamplePage(last, all[0], all.length),
    enabled: enabled && !!runRef,
    staleTime: jitteredStaleTime(GATE_CONTENT_STALE_MS),
    refetchOnWindowFocus: false,
    retry: retryContentRead,
  });
}

/** B10 — the review list's own filters content-gen applies on `enriched-products`. */
export type ReviewServerFilter = Pick<
  EnrichedProductsFilter,
  'search' | 'brand' | 'market' | 'reason' | 'preMin' | 'preMax' | 'postMin' | 'postMax'
>;

/** R1 + B10 — the review list's server-expressible filter: verdict, an explicit id set, the B10
 *  review filters (search / brand / market / reason / score band) and the order. */
export interface ReviewServerQuery {
  verdict: 'PASS' | 'REVIEW' | 'FAIL' | null;
  /** The spot-check set (≤ 100); failed products among them are read too. */
  productIds: readonly string[] | null;
  /** B10 filters — only sent once `useReviewServerFiltersSupport` proved the server applies them. */
  filter: ReviewServerFilter;
  order: EnrichedProductsFilter['order'];
  sortOrder: EnrichedProductsFilter['sortOrder'];
}

/** A search no product id, title or offer id can contain — the B10 capability probe's needle. */
const REVIEW_FILTER_PROBE = '~b10-capability-probe~';

/**
 * B10 capability check — does this content-gen apply the review list's server filters? Two
 * one-row reads: the unfiltered count and the count under a search nothing can match. A server
 * that honours `search` answers 0 there; an older one ignores it and answers the whole run (the
 * same "the total didn't narrow" tell `checkField` uses). Inconclusive on an empty run ⇒ false,
 * so the client walk stays the fallback. Cached for the run's review session.
 */
export function useReviewServerFiltersSupport(runRef: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogService.runContent(runRef, 'review-filter-capability'),
    queryFn: async (): Promise<boolean> => {
      const [all, probe] = await Promise.all([
        getEnrichedProducts(runRef, { page: 1, size: 1 }),
        getEnrichedProducts(runRef, { page: 1, size: 1, search: REVIEW_FILTER_PROBE }),
      ]);
      return all.total > 0 && probe.total === 0 && probe.items.length === 0;
    },
    enabled: enabled && !!runRef,
    staleTime: jitteredStaleTime(GATE_CONTENT_STALE_MS),
    refetchOnWindowFocus: false,
    // A failed probe (e.g. a 422 from an older validator) just means "use the client walk".
    retry: false,
  });
}

/** One server-filtered page of the review list, with its PRE / POST rows and failed members. */
export interface ReviewServerPage {
  page: CatalogPage<RunEnrichedProduct>;
  pre: Map<string, ProductScore>;
  post: Map<string, ProductScore>;
  /** Failed products of the spot-check set (empty under a verdict filter — failures have none). */
  failed: FailedProduct[];
  scoresUnavailable: boolean;
}

/** True when every row honours the filter — an older content-gen ignores `verdict` /
 *  `productIds` and answers the unfiltered grid, which this gives away. */
function honoursFilter(rows: readonly RunEnrichedProduct[], query: ReviewServerQuery): boolean {
  const ids = query.productIds ? new Set(query.productIds) : null;
  return rows.every(
    (r) => (!query.verdict || r.scores?.tier === query.verdict) && (!ids || ids.has(r.productId)),
  );
}

/**
 * R1 + B10 — one page of the review list filtered, ordered and paged on the SERVER
 * (`enriched-products` `verdict` / `productIds` / B10 filters / `order`, + `failed-products?
 * productIds` for a plain spot-check), with the page's PRE / POST score rows (two `/scores` reads
 * of ≤ `size` ids). Exact over the whole run, no 1,000-product cap. `null` when the server didn't
 * apply the verdict / id filter (older content-gen) — the lens then keeps its client sample.
 */
export function useRunReviewServerPage(
  runRef: string,
  query: ReviewServerQuery | null,
  page: number,
  size: number,
) {
  return useQuery({
    queryKey: queryKeys.catalogService.runContent(runRef, 'review-server-page', {
      ...query,
      page,
      size,
    }),
    queryFn: async ({ signal }): Promise<ReviewServerPage | null> => {
      if (!query) return null;
      const ids = query.productIds ?? undefined;
      // Failed products carry no title / brand / score, so a B10 filter never matches them.
      const plainSpotCheck = Object.keys(query.filter).length === 0;
      const [enriched, failed] = await Promise.all([
        getEnrichedProducts(runRef, {
          page,
          size,
          ...query.filter,
          ...(query.verdict ? { verdict: query.verdict } : {}),
          ...(ids ? { productIds: ids } : {}),
          ...(query.order ? { order: query.order, sortOrder: query.sortOrder } : {}),
        }),
        ids && !query.verdict && plainSpotCheck && page === 1
          ? getFailedProducts(runRef, { page: 1, size: REVIEW_PAGE_SIZE, productIds: ids })
          : Promise.resolve(null),
      ]);
      if (!honoursFilter(enriched.items, query)) return null;
      const failedRows = (failed?.items ?? []).filter((r) => !ids || ids.includes(r.productId));
      const pageIds = [...new Set(enriched.items.map((r) => r.productId))];
      const [pre, post] = await Promise.all([
        scoreRowsById(runRef, pageIds, 'PRE', signal),
        scoreRowsById(runRef, pageIds, 'POST', signal),
      ]);
      return {
        page: enriched,
        pre: pre.byId,
        post: post.byId,
        failed: failedRows,
        scoresUnavailable: pre.failed && post.failed,
      };
    },
    enabled: !!runRef && !!query,
    placeholderData: keepPreviousData,
    staleTime: jitteredStaleTime(GATE_CONTENT_STALE_MS),
    retry: retryContentRead,
  });
}
